import {useState, useRef, useEffect, forwardRef, useImperativeHandle, useContext} from "react";
import TextareaAutosize from 'react-textarea-autosize';
import {GlobalContext} from '../../../App';
import {URL_BASE} from "../../../Globals.ts";
import messageIdMapping from "../../../Utils/IdMappings/MessageIdMapping.ts";
import {useAppStore} from "../../../GlobalStore/GlobalStore.ts";
import {initializeNewChat} from "../../../Utils/db/chatHistory/chatMessagesUtils.ts";
import {FileObject, handleFileSelected} from "../../../Utils/uploadUtils/UploadUtils.ts";
import {addChild, setupUserMessage, toggleMessageImportance} from "../../../Utils/messageObject/messageObjectUtils.ts";
import {v4 as uuidv4} from "uuid";
import {useMessageQueue} from "../../../Utils/messageQueue/useMessageQueue.ts";

interface EditorProps {}

export interface EditorHandle {
    getText: () => string;
    getAttributes: (type: string) => { href?: string };
    commands: {
        clearContent: () => void;
        insertContent: (text: string) => void;
    };
    focus: () => void;
}

const InputEditor = forwardRef<EditorHandle, EditorProps>(({}, _) => {
    const { handleSubmitHelper, currentMessageRef, messagesSequenceRef, setMessages, messagesRef, llmRequestRefs, settingsObjRef, dynamicStateObjRef, userObjRef, websocketRef, setupWebsocketHelper, editorRef } = useContext(GlobalContext);
    const dynamicStateObj = useAppStore((state: any) => state.dynamicStateObj);
    const { canSubmit, isCurrentPromptImportant, mediaItemsInProgress } = dynamicStateObj;
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const updateSelectedMedia = useAppStore((state: any) => state.updateSelectedMedia);
    const updateChatHistory = useAppStore((state: any) => state.updateChatHistory);
    const environmentObj = useAppStore((state: any) => state.environmentObj);
    const { environment } = environmentObj;
    const settingsObj = useAppStore((state: any) => state.settingsObj);
    const { isIncognito } = settingsObj;
    const updateImportantMessageIds = useAppStore((state: any) => state.updateImportantMessageIds);
    const { enqueueMessage } = useMessageQueue();

    const [text, setText] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const selectedFilesRef = useRef(dynamicStateObj.selectedMedia);

    useEffect(() => {
        selectedFilesRef.current = dynamicStateObj.selectedMedia;
    }, [dynamicStateObj.selectedMedia]);

    const isValidUrl = (str: string): boolean => {
        try {
            new URL(str);
            return true;
        } catch {
            return false;
        }
    };

    const handleLinkPaste = async (link: string) => {
        const linkFileName = link.substring(link.indexOf('//') + 2);
        const existingFiles = Object.values(dynamicStateObj.selectedMedia) as FileObject[];
        if (existingFiles.some((file: FileObject) => file.fileName === linkFileName)) {
            return;
        }

        const fileId = uuidv4();
        const linkFile: FileObject = {
            fileId,
            fileName: linkFileName,
            loadingInfo: {
                task: 'starting'
            },
            fileContent: "",
            previewUrl: `https://icons.duckduckgo.com/ip3/${new URL(link).hostname}.ico`,
            type: "link"
        };
        setDynamicStateObj({
            selectedMedia: {
                ...dynamicStateObjRef.current.selectedMedia,
                [fileId]: linkFile
            }
        });

        try {
            const url = `${URL_BASE}/api/parseUrl`;

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ url: link })
            });

            if (response.ok) {
                const responseData = await response.json();
                const { parsedPageContent } = responseData;

                if (!parsedPageContent || parsedPageContent.trim() === "") {
                    const { [fileId]: _, ...remainingMedia } = dynamicStateObjRef.current.selectedMedia;
                    setDynamicStateObj({
                        selectedMedia: remainingMedia
                    });
                    return;
                }

                const updatedMedia = { ...dynamicStateObjRef.current.selectedMedia };
                if (updatedMedia[fileId]) {
                    updatedMedia[fileId] = {
                        ...updatedMedia[fileId],
                        fileContent: parsedPageContent,
                        loadingInfo: {
                            task: 'done'
                        },
                    };
                }

                setDynamicStateObj({
                    selectedMedia: updatedMedia,
                    hasAttachment: true
                });
            } else {
                const { [fileId]: _, ...remainingMedia } = dynamicStateObjRef.current.selectedMedia;
                setDynamicStateObj({
                    selectedMedia: remainingMedia
                });
            }
        } catch (error) {
            const { [fileId]: _, ...remainingMedia } = dynamicStateObjRef.current.selectedMedia;
            setDynamicStateObj({
                selectedMedia: remainingMedia
            });
        }
    };

    const parseInput = async (inputText: string | undefined, files: Record<string, FileObject>) => {
        if (mediaItemsInProgress.length > 0) {
            return;
        }

        if (!canSubmit) {
            const messageId = enqueueMessage(inputText || '', files, isCurrentPromptImportant);
            console.log(`Message queued with ID: ${messageId}`);
            return;
        }

        if (environmentObj.environment.includes("jetbrains") && inputText === "__open_devtools__") {
            window.jetbrainsPostMessage(JSON.stringify({
                functionName: "openDevTools",
                data: []
            }));
        }

        let conversationId = dynamicStateObjRef.current.conversationId;
        if (Object.keys(messageIdMapping).length === 0) {
            conversationId = uuidv4();
            initializeNewChat(inputText ?? "_", isIncognito, userObjRef.current.user, setDynamicStateObj, updateChatHistory, setMessages, messagesRef, currentMessageRef, conversationId);
        }

        const content = [];
        const filesList = Object.values(files);
        const filteredFiles = filesList.filter((file: FileObject) => !(file.type === "link" && file.loadingInfo.task !== "done"));

        if (filteredFiles.length !== 0) {
            const sortedFiles = filteredFiles.sort((a: FileObject, b: FileObject) => {
                const typeOrder: Record<string, number> = { 'video': 1, 'image': 2, 'audio': 3, 'file': 3, 'link': 4 };
                return (typeOrder[a.type || 'file']) - (typeOrder[b.type || 'file']);
            });
            content.push(...sortedFiles);
        }

        if (inputText) {
            content.push({
                type: 'text',
                text: inputText
            });
        }

        const userMessage = setupUserMessage(content);
        llmRequestRefs.current.parentMessage = currentMessageRef.current;
        const messageId = uuidv4();
        addChild(userMessage, currentMessageRef, messagesSequenceRef, messageId);
        llmRequestRefs.current.messagesToWrite.push(currentMessageRef.current);
        setDynamicStateObj({isCurrentPromptImportant: false});
        if (isCurrentPromptImportant) toggleMessageImportance(userObjRef.current.user, settingsObjRef.current.isIncognito, conversationId, messageId, isCurrentPromptImportant, updateImportantMessageIds, setMessages);
        handleSubmitHelper();
    };

    const handlePaste = async (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const items = event.clipboardData?.items;
        if (!items) return;

        const files: File[] = [];
        let pastedText = '';

        for (let i = 0; i < items.length; i++) {
            if (items[i].kind === 'file') {
                const file = items[i].getAsFile();
                if (file) {
                    files.push(file);
                }
            } else if (items[i].kind === 'string' && items[i].type === 'text/plain') {
                pastedText = await new Promise<string>((resolve) => {
                    items[i].getAsString((str) => resolve(str));
                });
            }
        }

        // Handle file pastes
        if (files.length > 0) {
            for (const file of files) {
                await handleFileSelected(file, userObjRef, settingsObjRef, updateSelectedMedia, websocketRef, setupWebsocketHelper, setDynamicStateObj);
            }
            event.preventDefault();
            return;
        }

        // Handle text paste - check if it's a URL
        if (pastedText && isValidUrl(pastedText)) {
            await handleLinkPaste(pastedText);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        const isMobile = environment.includes('mobile');

        // On mobile, only submit via synthetic event from Send button (isTrusted=false)
        // Real keyboard Enter presses (isTrusted=true) should just create newlines
        if (isMobile && event.key === 'Enter' && event.nativeEvent.isTrusted) {
            return; // Allow default behavior (newline) for real keyboard presses
        }

        const shouldSubmit = event.key === 'Enter' && ((!event.shiftKey && !isMobile) || (event.shiftKey && isMobile));

        if (shouldSubmit) {
            event.preventDefault();

            const currentText = text.trim();
            const hasContent = currentText !== '' || Object.keys(selectedFilesRef.current).length !== 0;

            if (hasContent) {
                if (mediaItemsInProgress.length > 0) {
                    return;
                }

                parseInput(currentText, selectedFilesRef.current);
                setDynamicStateObj({hasAttachment: false, hasText: false});
                updateSelectedMedia(null, 'clear');
                setText('');
            }
        }
    };


    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newText = event.target.value;
        setText(newText);
        setDynamicStateObj({hasText: newText.trim() !== ''});
    };

    // Expose editor-like API through ref
    useImperativeHandle(editorRef, () => ({
        getText: () => text,
        getAttributes: () => ({}),
        commands: {
            clearContent: () => {
                setText('');
                setDynamicStateObj({hasText: false});
            },
            insertContent: (content: string) => {
                setText(prev => prev + content);
                setDynamicStateObj({hasText: true});
            }
        },
        focus: () => textareaRef.current?.focus()
    }), [text]);

    return (
        <TextareaAutosize
            ref={textareaRef}
            value={text}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder="Type a message..."
            className={`inputEditor break-words text-neutral-900 dark:text-neutral-200 placeholder:text-neutral-600 dark:placeholder:text-neutral-300 max-h-[200px] w-full resize-none bg-transparent overflow-y-auto focus:outline-none ${environment.includes("jetbrains") || environment === "chrome" || environment === "vscode" ? "my-1 pt-1" : "my-1 pt-1.5"}`}
            minRows={1}
            maxRows={10}
        />
    );
});

export default InputEditor;
